import { Button } from "@mui/material";
import { useEffect, useState } from "react";

// UI
import {
  BootstrapDialog,
  BootstrapDialogInnerContainer,
  DialogActionsButtonContainer,
} from "../../chat.styled";
import CheckboxChecked from "assets/CheckboxChecked";
import CheckboxUnchecked from "assets/CheckboxUnchecked";
import { useTheme } from "styled-components";
import { AnimatePresence, motion } from "framer-motion";
import { Form, FormField } from "../../chat.type";
import { Socket } from "socket.io-client";

interface SuggestedPromptFormProps {
  activeForm: Form;
  setActiveForm: (form: Form | null) => void;
  handleInputSelect: (input: string) => void;
  socket: typeof Socket;
}

const field_mapping = {
  "text": "_textbox",
  "textarea": "_textarea"
}

export function SuggestedPromptForm({
  activeForm,
  setActiveForm,
  handleInputSelect,
  socket,
}: SuggestedPromptFormProps) {
  const [expandedFields, setExpandedFields] = useState<Record<string, boolean>>({});
  const theme = useTheme();

  // Set the initial form state
  const [formState, setFormState] = useState<Record<string, any>>(() => {
    const initialState: Record<string, any> = {};

    if (activeForm?.fields) {
      const initializeFields = (fields: FormField[]) => {
        fields.forEach((field) => {
          if (field.type === "checkbox") {
            initialState[field.name] = !!field.defaultChecked;
            if (field.showFieldsOnCheck)
              initializeFields(field.showFieldsOnCheck);
            if (field.showFieldsOnUncheck)
              initializeFields(field.showFieldsOnUncheck);
          } else if (field.type === "text") {
            initialState[`${field.name}${field_mapping[field.type]}`] = "";
          } else if (field.type === "textarea") {
            initialState[`${field.name}${field_mapping[field.type]}`] = "";
          }
        });
      };
      initializeFields(activeForm.fields as FormField[]);
    }
    
    // Add _textbox, or _textarea depending on the field types
    return initialState;
  });

  useEffect(() => {
    if (activeForm?.fields) {
      const initialState: Record<string, any> = {};
      const initialExpanded: Record<string, boolean> = {};

      const initializeFields = (fields: FormField[]) => {
        fields.forEach((field) => {
          if (field.type === "checkbox") {
            initialState[field.name] =
              field.name === "name" ? true : !!field.defaultChecked;
            if (
              field.showFieldsOnCheck?.length ||
              field.showFieldsOnUncheck?.length
            ) {
              initialExpanded[field.name] = false;
            }
            if (field.showFieldsOnCheck)
              initializeFields(field.showFieldsOnCheck);
            if (field.showFieldsOnUncheck)
              initializeFields(field.showFieldsOnUncheck);
          } else if (field.type === "text") {
            initialState[`${field.name}${field_mapping[field.type]}`] = "";
          } else if (field.type === "textarea") {
            initialState[`${field.name}${field_mapping[field.type]}`] = "";
          }
        });
      };

      initializeFields(activeForm.fields as FormField[]);
      setFormState(initialState);
      setExpandedFields(initialExpanded);
    }
  }, [activeForm]);

  /**
   * Handles the field changes
   * @param name field name
   * @param value field value
   */
  const handleChange = (name: string, value: any) => {
    setFormState((prev) => {
      const newState = { ...prev, [name]: value };

      // Helper to recursively find parent and update its checked state
      function updateParentChecked(fields, changedName, topLevelFields) {
        for (const field of fields) {
          if (field.showFieldsOnUncheck || field.showFieldsOnCheck) {
            const children = [
              ...(field.showFieldsOnUncheck || []),
              ...(field.showFieldsOnCheck || []),
            ];
            if (children.some((child) => child.name === changedName)) {
              // This field is the parent of changedName
              const anyChecked = children.some((child) => newState[child.name]);

              // Only update parent if it's NOT a top-level field, or if we're checking (not unchecking)
              if (
                !topLevelFields.some((f) => f.name === field.name) ||
                anyChecked
              ) {
                newState[field.name] = anyChecked;
              }
            }
            // Recursively check deeper nesting
            updateParentChecked(children, changedName, topLevelFields);
          }
        }
      }

      // Call the helper with your top-level fields
      updateParentChecked(activeForm.fields, name, activeForm.fields);

      return newState;
    });
  };

  /**
   * When the form is submitted we use the form's 'textOutput' string to generate the userinput to the provider
   * @param e 
   */
  const handleFormSubmit = (e) => {
    const flattenedState = { ...formState };

    // Remove the _textbox and _textarea from keys so we can match the fields, in this form's 'textOutput' string
    Object.keys(formState).forEach((key) => {
      if (key.endsWith("_textbox")) {
        const baseKey = key.replace("_textbox", "");
        flattenedState[baseKey] = formState[key];
      } else if (key.endsWith("_textarea")) {
        const baseKey = key.replace("_textarea", "");
        flattenedState[baseKey] = formState[key];
      }
    });

    e.preventDefault();
    
    // The String Interpolated output of this form's 'textOutput' string
    const generatedText = activeForm.textOutput(flattenedState);
    
    // Send the prompt to the provider
    try {
      if (socket && socket.connected) {
        socket.emit("send_prompt", {
          prompt: generatedText,
          timestamp: new Date().toISOString(),
        });
        handleInputSelect(generatedText);
        setActiveForm(null);
      }
    } catch (error) {
      console.error("Error sending prompt:", error);
    }
  };

  /**
   * Expand nexted checkbox forms
   * @param name 
   */
  const toggleExpand = (name: string) => {
    setExpandedFields((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  /**
   * Render fields from the form: text, textarea, checkbox
   * @param field 
   * @param isNested 
   * @returns 
   */
  const renderField = (
    field: FormField,
    isNested: boolean = false
  ): JSX.Element | null => {
    const {
      label,
      name,
      type,
      showFieldsOnCheck = [],
      showFieldsOnUncheck = [],
      showTextFieldsOnCheck = [],
      textboxPlaceholder,
    } = field;

    // Used as a boolean for checkbox situations pure existance for other situations
    const isChecked = type === "checkbox"
      ? formState[name]
      : formState[`${name}${field_mapping[type]}`];

    const hasSubNestedFields = field.showFieldsOnUncheck?.some(
      (child) => child.showFieldsOnCheck
    );

    const hasNestedFields: boolean =
      showFieldsOnCheck.length > 0 || showFieldsOnUncheck.length > 0;

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      handleChange(name, checked);
      const updateChildren = (fields: FormField[]) => {
        fields.forEach((child) => {
          handleChange(child.name, checked);
          if (child.showFieldsOnCheck?.length)
            updateChildren(child.showFieldsOnCheck);
          if (child.showFieldsOnUncheck?.length)
            updateChildren(child.showFieldsOnUncheck);
        });
      };
      updateChildren(showFieldsOnCheck);
      updateChildren(showFieldsOnUncheck);
    };

    if (!activeForm || Object.keys(formState).length === 0) {
      return <div>Loading form...</div>; // or null, or spinner etc
    }

    if (type === "checkbox") {
      return (
        <div key={name} style={{ marginLeft: isNested ? "20px" : "0" }}>
          <div className="flex p-[0.5rem]">
            <label
              onClick={() =>
                handleCheckboxChange({ target: { checked: !isChecked } } as any)
              }
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
              }}
            >
              <span style={{ height: "14px", display: "contents" }}>
                <div style={{ transform: "scale(0.75)" }}>
                  {isChecked ? (
                    <CheckboxChecked />
                  ) : (
                    <CheckboxUnchecked theme={theme} />
                  )}
                </div>
              </span>
              <span>{label}</span>
            </label>

            {hasSubNestedFields && isNested && (
              <motion.span
                onClick={() => toggleExpand(name)}
                animate={{ rotate: expandedFields[name] ? 90 : 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  display: "inline-block",
                  cursor: "pointer",
                  marginLeft: "1em",
                  fontSize: "16px",
                  color: "#999",
                }}
              >
                ▶
              </motion.span>
            )}
          </div>

          {isChecked && showFieldsOnCheck.map((child) => renderField(child, true))}
          <AnimatePresence initial={false}>
            {hasNestedFields && expandedFields[name] && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {showFieldsOnUncheck.map((child) => renderField(child, true))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }

    if (type === "text") {
      return (
        <div key={name} style={{ marginLeft: isNested ? "20px" : "0" }}>
          {textboxPlaceholder ? (
            isChecked !== undefined && textboxPlaceholder ? (
              <>
                <label style={{ display: "block", marginBottom: "10px" }}>
                  {label}
                </label>
                <input
                  type="text"
                  value={formState[`${name}${field_mapping[type]}`] || ""}
                  onChange={(e) =>
                    handleChange(`${name}${field_mapping[type]}`, e.target.value)
                  }
                  placeholder={textboxPlaceholder}
                  style={{
                    margin: "2px 0 5px 20px",
                    padding: "5px",
                    width: "calc(100% - 40px)",
                  }}
                />
              </>
            ) : <>boop</>
          ) : (
            <>
              <label style={{ display: "block", marginBottom: "10px" }}>
                {label}
              </label>
              <input
                style={{ marginBottom: "10px", width: "calc(100% - 40px)" }}
                type="text"
                value={formState[`${name}${field_mapping[type]}`] || ""}
                onChange={(e) =>
                  handleChange(`${name}${field_mapping[type]}`, e.target.value)
                }
                placeholder={label}
              />
            </>
          )}
        </div>
      );
    }

    if (type === "textarea") {
      return (
        <div key={name} style={{ marginLeft: isNested ? "20px" : "0" }}>
          {textboxPlaceholder ? (
            isChecked !== undefined && textboxPlaceholder ? (
              <textarea
                value={formState[`${name}${field_mapping[type]}`] || ""}
                onChange={(e) =>
                  handleChange(`${name}${field_mapping[type]}`, e.target.value)
                }
                placeholder={textboxPlaceholder}
                style={{
                  margin: "2px 0 5px 20px",
                  padding: "5px",
                  width: "calc(100% - 40px)",
                }}
                rows={5}
              />
            ) : null
          ) : (
            <>
              <label style={{ display: "block", marginBottom: "10px" }}>
                {label}
              </label>
              <textarea
                style={{ marginBottom: "10px", width: "calc(100% - 40px)" }}
                value={formState[`${name}${field_mapping[type]}`] || ""}
                onChange={(e) =>
                  handleChange(`${name}${field_mapping[type]}`, e.target.value)
                }
                placeholder={label}
                rows={5}
              />
            </>
          )}
        </div>
      );
    }

    return null;
  };

  const l1LabelField =
    activeForm?.fields?.[0]?.showFieldsOnCheck?.[0]?.showFieldsOnUncheck?.find(
      (field) => field.label === "L1" && field.type === "checkbox"
    );

  const l1CheckboxName = l1LabelField?.name;
  const l1TextboxName = l1LabelField?.showFieldsOnCheck?.[0]?.name;

  const isL1Checked = !!formState[l1CheckboxName as keyof typeof formState];
  const isL1TextboxFilled =
    !!formState[`${l1TextboxName}_textbox`] &&
    formState[`${l1TextboxName}_textbox`].trim().length > 0;

  const disableSubmit = false;

  return (
    <BootstrapDialog
      open={Boolean(activeForm)}
      onClose={() => setActiveForm(null)}
      sx={{ borderRadius: "1rem" }}
    >
      <BootstrapDialogInnerContainer elevation={2}>
        <div className="p-2">
          <h3 className="ms-3">{activeForm?.title}</h3>
        </div>

        <form onSubmit={handleFormSubmit}>
          <div
            className="p-4 mt-[-1.5rem] mb-[-1.5rem]"
            style={{
              maxHeight: "55vh",
              overflowY: "auto",
            }}
          >
            <motion.div
              variants={{
                hidden: { opacity: 0 },
                show: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.05,
                  },
                },
              }}
              initial="hidden"
              animate="show"
            >
              {activeForm?.fields.map((child) => (
                <motion.div
                  key={child.name}
                  variants={{
                    hidden: { opacity: 0, y: 5 },
                    show: { opacity: 1, y: 0 },
                  }}
                >
                  {renderField(child as FormField, false)}
                </motion.div>
              ))}
            </motion.div>
          </div>
          <DialogActionsButtonContainer>
            <Button
              color="secondary"
              autoFocus
              variant="contained"
              type="submit"
              disabled={disableSubmit} // ❗ disables the button
              sx={{
                opacity: disableSubmit ? 0.5 : 1, // makes it appear grayed out
                pointerEvents: disableSubmit ? "none" : "auto", // avoids hover effect or tooltips if needed
                cursor: disableSubmit ? "not-allowed" : "pointer",
              }}
            >
              {activeForm?.submitLabel}
            </Button>
          </DialogActionsButtonContainer>
        </form>
      </BootstrapDialogInnerContainer>
    </BootstrapDialog>
  );
}
